import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  SlideUpTransition,
  useMinimizable,
  MinimizeButton,
  MinimizedBar,
} from '../../components/MinimizableDialog';
import {
  DemandeAchat,
  DemandeAchatCreate,
  DemandeAchatUpdate,
  DemandeAchatItemCreate,
} from '../../types/demandeAchat';
import { Article } from '../../types';
import { Projet } from '../../types/projet';
import { demandesAchatAPI, articlesAPI, projetsAPI } from '../../services/api';
import AlertDialog from '../../components/AlertDialog';
import useAlert from '../../hooks/useAlert';
import useFormDraft from '../../hooks/useFormDraft';
import { extractErrorMessage } from '../../utils/errorHandler';

interface LineItem {
  article_id: number | null;
  designation: string;
  quantite: number | null | string;
  commentaire: string;
}

interface DemandeAchatFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  demandeAchatId?: number;
  // Optional controlled minimize: when the parent owns this, clicking the
  // "Add"/"Edit" trigger again can restore the minimized dialog. Falls back to
  // internal state when not provided.
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
}

const STOCK_COLOR = '#2196F3';

const emptyItem = (): LineItem => ({
  article_id: null,
  designation: '',
  quantite: null,
  commentaire: '',
});

const DemandeAchatFormDialog: React.FC<DemandeAchatFormDialogProps> = ({
  open,
  onClose,
  onSuccess,
  demandeAchatId,
  minimized: controlledMinimized,
  onMinimizedChange,
}) => {
  const isEdit = !!demandeAchatId;

  const [loading, setLoading] = useState(false);
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(
    open,
    controlledMinimized,
    onMinimizedChange
  );
  const [articles, setArticles] = useState<Article[]>([]);
  const [projets, setProjets] = useState<Projet[]>([]);
  const [selectedProjet, setSelectedProjet] = useState<Projet | null>(null);
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [lineItems, setLineItems] = useState<LineItem[]>([emptyItem()]);
  const [nextReference, setNextReference] = useState<string>('');
  const [editingReference, setEditingReference] = useState<string>('');

  const { alertState, showError, showSuccess, hideAlert } = useAlert();

  // Draft caching — only for new demandes d'achat (never when editing an existing row).
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    date: string;
    selectedProjet: Projet | null;
    lineItems: LineItem[];
  }>('demande-achat', !isEdit);

  const resetForm = () => {
    setDate(new Date().toISOString().split('T')[0]);
    setLineItems([emptyItem()]);
    setEditingReference('');
    setSelectedProjet(null);
  };

  // Load articles + projets dropdowns
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [articlesData, projetsData] = await Promise.all([
          articlesAPI.getAllForDropdown(),
          projetsAPI.getAllForDropdown(),
        ]);
        setArticles(articlesData);
        setProjets(projetsData);
      } catch (err) {
        console.error('Erreur chargement données', err);
      }
    })();
  }, [open]);

  // Load existing DA when editing, OR reset for create
  useEffect(() => {
    if (!open) return;
    if (!demandeAchatId) {
      const draft = loadDraft();
      if (draft) {
        setDate(draft.date);
        setSelectedProjet(draft.selectedProjet);
        setLineItems(draft.lineItems);
        setEditingReference('');
      } else {
        resetForm();
      }
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const da: DemandeAchat = await demandesAchatAPI.getById(demandeAchatId);
        setDate(da.date);
        setEditingReference(da.reference);
        // Selected projet is resolved from the loaded projets list
        const matchingProjet = projets.find((p) => p.id === da.projet_id) || null;
        setSelectedProjet(
          matchingProjet ||
            (da.projet_id
              ? ({ id: da.projet_id, code: da.projet_code || '', nom: da.projet_nom || '' } as Projet)
              : null),
        );
        setLineItems(
          da.items.length === 0
            ? [emptyItem()]
            : da.items.map((it) => ({
                article_id: it.article_id ?? null,
                designation: it.designation,
                quantite: typeof it.quantite === 'string' ? parseFloat(it.quantite) : it.quantite,
                commentaire: it.commentaire ?? '',
              })),
        );
      } catch (err: any) {
        showError(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [open, demandeAchatId, projets]);

  // Fetch the next reference preview when the dialog opens in create mode (or when date year changes)
  useEffect(() => {
    if (!open || isEdit || !date) {
      setNextReference('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await demandesAchatAPI.getNextReference(date);
        if (!cancelled) setNextReference(res.reference);
      } catch (err) {
        if (!cancelled) setNextReference('');
        console.error('Failed to fetch next DA reference', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isEdit, date]);

  const handleItemChange = (index: number, field: keyof LineItem, value: any) => {
    setLineItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleArticleSelect = (index: number, article: Article) => {
    setLineItems((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        article_id: article.id,
        designation: article.designation,
      };
      return next;
    });
  };

  const addLineItem = () => setLineItems((prev) => [...prev, emptyItem()]);

  const removeLineItem = (index: number) => {
    if (lineItems.length === 1) return;
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    // Validate items
    const validItems: DemandeAchatItemCreate[] = [];
    for (const item of lineItems) {
      const qte =
        typeof item.quantite === 'string'
          ? parseFloat(item.quantite.replace(',', '.'))
          : item.quantite;
      if (item.designation && item.designation.trim() !== '' && qte && qte > 0) {
        validItems.push({
          article_id: item.article_id,
          designation: item.designation.trim(),
          quantite: qte,
          commentaire: item.commentaire?.trim() || null,
        });
      }
    }

    if (validItems.length === 0) {
      showError('Veuillez ajouter au moins un article avec une désignation et une quantité valides');
      return;
    }

    if (!date) {
      showError('Veuillez sélectionner une date');
      return;
    }

    if (!selectedProjet) {
      showError('Veuillez sélectionner un projet');
      return;
    }

    try {
      setLoading(true);
      if (isEdit && demandeAchatId) {
        const payload: DemandeAchatUpdate = {
          date,
          projet_id: selectedProjet.id,
          items: validItems,
        };
        await demandesAchatAPI.update(demandeAchatId, payload);
        showSuccess('Demande d\'achat mise à jour avec succès');
      } else {
        const payload: DemandeAchatCreate = {
          date,
          projet_id: selectedProjet.id,
          items: validItems,
        };
        await demandesAchatAPI.create(payload);
        showSuccess('Demande d\'achat créée avec succès');
      }
      clearDraft();
      onSuccess();
      onClose();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // X / ANNULER = deliberate cancel: wipe inputs and discard any draft.
  const handleClose = () => {
    clearDraft();
    resetForm();
    setMinimized(false);
    onClose();
  };

  // Backdrop click / Escape = accidental: keep the work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      saveDraft({ date, selectedProjet, lineItems });
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="lg"
        fullWidth
        TransitionComponent={SlideUpTransition}
      >
        <DialogTitle sx={{ bgcolor: STOCK_COLOR, color: 'white', py: 1.25 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {isEdit ? "Modifier la demande d'achat" : "Nouvelle demande d'achat"}
              {(isEdit ? editingReference : nextReference) && (
                <Typography component="span" variant="caption" sx={{ ml: 1.5, color: 'rgba(255,255,255,0.85)', fontWeight: 600, letterSpacing: 0.5 }}>
                  N° DA: {isEdit ? editingReference : nextReference}
                </Typography>
              )}
            </Typography>
            <Box display="flex" alignItems="center">
              <MinimizeButton color="white" onClick={minimize} />
              <IconButton size="small" onClick={handleClose} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2} sx={{ mb: 2, mt: 0 }} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                label="Date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Autocomplete
                options={projets}
                value={selectedProjet}
                onChange={(_, val) => setSelectedProjet(val)}
                getOptionLabel={(opt) => `${opt.code} - ${opt.nom}`}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Projet *"
                    size="small"
                    placeholder="Sélectionner un projet"
                    required
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{option.code}</Typography>
                      <Typography variant="caption" color="text.secondary">{option.nom}</Typography>
                    </Box>
                  </li>
                )}
                size="small"
              />
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" sx={{ color: STOCK_COLOR, fontWeight: 600 }}>
              Articles demandés
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={addLineItem}
              sx={{ borderColor: STOCK_COLOR, color: STOCK_COLOR }}
            >
              Ajouter un article
            </Button>
          </Box>

          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.default' }}>
                  <TableCell sx={{ width: '45%' }}>Désignation</TableCell>
                  <TableCell sx={{ width: '15%' }} align="right">Quantité</TableCell>
                  <TableCell sx={{ width: '30%' }}>Commentaire</TableCell>
                  <TableCell sx={{ width: '10%' }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Autocomplete
                        freeSolo
                        options={articles}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          return `${option.reference} - ${option.designation}`;
                        }}
                        value={
                          item.article_id
                            ? articles.find((a) => a.id === item.article_id) || null
                            : item.designation || ''
                        }
                        onChange={(_, value) => {
                          if (typeof value === 'string') {
                            handleItemChange(index, 'designation', value);
                            handleItemChange(index, 'article_id', null);
                          } else if (value) {
                            handleArticleSelect(index, value);
                          } else {
                            handleItemChange(index, 'designation', '');
                            handleItemChange(index, 'article_id', null);
                          }
                        }}
                        onBlur={(e) => {
                          const inputValue = (e.target as HTMLInputElement).value;
                          if (inputValue && !item.article_id && inputValue !== item.designation) {
                            handleItemChange(index, 'designation', inputValue);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            multiline
                            rows={2}
                            placeholder="Sélectionner ou saisir une désignation"
                            sx={{
                              '& .MuiInputBase-root': { alignItems: 'flex-start' },
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option.id}>
                            <Box>
                              <Typography variant="body2">
                                {option.reference} - {option.designation}
                              </Typography>
                            </Box>
                          </li>
                        )}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="text"
                        size="small"
                        value={item.quantite ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            handleItemChange(index, 'quantite', null);
                          } else if (/^\d*[.,]?\d*$/.test(value)) {
                            handleItemChange(index, 'quantite', value);
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value && value !== '') {
                            const num = parseFloat(value.replace(',', '.'));
                            handleItemChange(index, 'quantite', isNaN(num) ? null : num);
                          }
                        }}
                        fullWidth
                        inputProps={{ style: { textAlign: 'right' } }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={item.commentaire}
                        onChange={(e) => handleItemChange(index, 'commentaire', e.target.value)}
                        placeholder="Optionnel"
                        fullWidth
                        multiline
                        rows={2}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => removeLineItem(index)}
                        disabled={lineItems.length === 1}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Annuler
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
            sx={{ bgcolor: STOCK_COLOR, '&:hover': { bgcolor: '#1E88E5' } }}
          >
            {isEdit ? 'Mettre à jour' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <MinimizedBar
        open={open && minimized}
        color={STOCK_COLOR}
        title={`${isEdit ? "Modifier la demande d'achat" : "Nouvelle demande d'achat"}${
          (isEdit ? editingReference : nextReference)
            ? ` — ${isEdit ? editingReference : nextReference}`
            : ''
        }`}
        onRestore={restore}
        onClose={handleClose}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
      />
    </>
  );
};

export default DemandeAchatFormDialog;
